import { useState, useEffect } from 'react';

/**
 * Custom hook to check if a feature flag is enabled
 * Reads feature flags from API responses or window object
 * 
 * @param {string} flagName - Feature flag name (e.g., 'freeShippingToIsraelUI')
 * @param {Object} options - Options object
 * @param {Object} options.apiResponse - API response object that may contain featureFlags
 * @param {boolean} options.defaultValue - Default value if flag is not found (default: false)
 * @returns {boolean} Whether the feature flag is enabled
 * 
 * @example
 * const freeShippingUIEnabled = useFeatureFlag('freeShippingToIsraelUI', { apiResponse: searchResults });
 */
export const useFeatureFlag = (flagName, { apiResponse = null, defaultValue = false } = {}) => {
  const [isEnabled, setIsEnabled] = useState(defaultValue);

  useEffect(() => {
    const checkFlag = () => {
      // Check if feature flag is in API response
      if (apiResponse?.featureFlags?.[flagName] !== undefined) {
        setIsEnabled(apiResponse.featureFlags[flagName]);
        return;
      }

      // Check if feature flag is in window object (set by server-side rendering or initial load)
      if (window.featureFlags?.[flagName] !== undefined) {
        setIsEnabled(window.featureFlags[flagName]);
        return;
      }

      // Default to false if not found
      setIsEnabled(defaultValue);
    };

    checkFlag();

    // window.featureFlags is populated asynchronously (Hero.jsx/MyTrackingPage.jsx/
    // UserMenu.jsx all set it once their own fetch resolves) - a consumer that mounts
    // before that fetch lands would otherwise be stuck on its first (false) check
    // forever, since nothing re-runs this effect on its own. Each of those call sites
    // dispatches this event right after updating window.featureFlags.
    document.addEventListener('featureFlagsUpdated', checkFlag);
    return () => document.removeEventListener('featureFlagsUpdated', checkFlag);
  }, [flagName, apiResponse, defaultValue]);

  return isEnabled;
};

/**
 * Utility function to check feature flag from API response
 * Useful for one-time checks outside of React components
 * 
 * @param {string} flagName - Feature flag name
 * @param {Object} apiResponse - API response object
 * @param {boolean} defaultValue - Default value if flag is not found
 * @returns {boolean} Whether the feature flag is enabled
 */
export const getFeatureFlag = (flagName, apiResponse = null, defaultValue = false) => {
  if (apiResponse?.featureFlags?.[flagName] !== undefined) {
    return apiResponse.featureFlags[flagName];
  }
  
  if (window.featureFlags?.[flagName] !== undefined) {
    return window.featureFlags[flagName];
  }
  
  return defaultValue;
};

